import { toAbsoluteUrl } from "../../_theme/helpers";
import {
    TrendingUpIcon,
    ClipboardListIcon,
    FolderOpenIcon,
    CheckCircleIcon
} from "lucide-react";
import { memo } from "react";
import HeroSection from "./Components/HeroSection";
import { envConfig } from "../../utils/config";
import { useDashboardTabEntry } from "../modules/Dashboard/core/tabRights";

// Types
interface PipelineStep {
    id: number;
    number: string;
    title: string;
    description: string;
    icon: React.ReactNode;
}

interface Feature {
    id: number;
    text: string;
}

// Constants
const FEATURES_DATA: Feature[] = [
    {
        id: 1,
        text: "Unified Project Pipelines: Instantly explore a dynamic landscape of concepts drawn from municipalities and provincial departments, all in one place."
    },
    {
        id: 2,
        text: "Smart discovery and use of Filters: Select sector, priority, or policy-aligned filters to surface projects that truly matter to you."
    },
    {
        id: 3,
        text: "Comprehensive Readiness Checks: Evaluate each initiative's technical strength, financial soundness, regulatory fit, and investment appeal - enhanced by gamified scoring and AI-powered tools that reduce costs and streamline document compilation."
    },
    {
        id: 4,
        text: "Tracking and Visualisation: Follow the complete project lifecycle - from the initial spark of an idea to investment-ready status - using the Gigamap. This dynamic visual tool makes complex systems understandable, mapping relationships, stakeholders, and milestones with transparency at every stage."
    }
];

const ENGAGEMENT_DATA: Feature[] = [
    {
        id: 1,
        text: "Dynamic Compliance Scoring: Instantly see how projects align with evolving policy, legal, and sustainability goals."
    },
    {
        id: 2,
        text: "Interactive Evaluation Maps: Visualise projects across the Western Cape with geospatial tools that reveal the bigger picture."
    },
    {
        id: 3,
        text: "Predictive Red-Flag Alerts: Get ahead of obstacles with early risk indicators."
    }
];

const PIPELINE_STEPS: PipelineStep[] = [
    {
        id: 1,
        number: "1",
        title: "Existing Pipelines",
        description: "Scan the existing project pipeline to support the Prioritised pipeline step.",
        icon: <TrendingUpIcon size={32} />
    },
    {
        id: 2,
        number: "2",
        title: "Prioritised Pipeline",
        description: "Identify a Prioritised pipeline using policy (Prescripts) and strategic compliances to support the evaluation.",
        icon: <ClipboardListIcon size={32} />
    },
    {
        id: 3,
        number: "3",
        title: "Project Evaluation & Prepackaging",
        description: "Refine projects by applying the Panoptic Principles as a project evaluation criteria to support Investor-ready steps.",
        icon: <CheckCircleIcon size={32} />
    },
    {
        id: 4,
        number: "4",
        title: "Pipeline Portfolio",
        description: "Package the pipeline into a bankable and investment ready portfolio.",
        icon: <FolderOpenIcon size={32} />
    }
];

const IntroductionSection: React.FC = memo(() => (
    <div className="row m-0 section-padding bg-white section-padding-bottom-0" id="introduction-section">
        <div className="col-md-12 px-10 px-sm-15 px-md-10 px-lg-20">
            <p className="penoptic-evaluation-section-title mb-3">Turning Vision into Value – Through collaboration and empathic design</p>
            <p className="section-content">
                Project evaluation offers clarity, strategy, and impact. The Evaluation tool uses the Panoptic Principles that refers to transversal and encompassing policy-alignment. Designed to offer a rigorous assessment of infrastructure projects across social, economic, spatial, governance, and sustainability dimensions.
            </p>
        </div>
    </div>
));

const PipelineCard: React.FC<{ step: PipelineStep }> = memo(({ step }) => {
    return (
        <div className="infrastructure-box">
            <div className="box">
                {/* Normal Content */}
                <div className='icon'>
                    <h3 className="title fs-2 fw-bold text-dark mb-1">{step.title}</h3>
                </div>

                {/* Hover Content */}
                <div className='content'>
                    <h4 className="fs-3 fw-bold dark mb-2 px-5">{step.title}</h4>
                    <p className="fs-5 px-5">{step.description}</p>
                </div>

                <div className="box-number border-none">
                    <div className="number-round">
                        <span className="number">{step.number}</span>
                    </div>
                </div>
            </div>
        </div>
    );
});

const CardsSection: React.FC = memo(() => (
    <div className="row section-padding bg-white">
        <div className="col-12  px-10 px-sm-15 px-md-10 px-lg-20">
            <p className="penoptic-evaluation-section-title mb-3">
                Four Foundational steps that supports the trusted investor-ready project pipeline
            </p>
            <div
                className="py-20"
            >
                <div className="public-pages-container px-0">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="infrastructure-boxes">
                                {PIPELINE_STEPS.map((card) => (
                                    <div key={card.id}>
                                        <PipelineCard step={card} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
));

const OutcomeSection: React.FC = memo(() => (
    <div className="row d-flex flex-column-reverse flex-lg-row section-padding g-10 bg-white align-items-center m-0">
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20  pe-lg-5 pe-xl-5">
            <div>
                <p className="penoptic-evaluation-section-title mb-2">An Investment-Ready Infrastructure Portfolio</p>
                <p className="section-content">
                    When projects are certified through panoptic criteria they are prioritised and packaged into a formal Investment-Ready Portfolio. This is a vital step to develop trusted and transparent pipelines that invites meaningful engagement from financiers and private-sector partners. The platform transforms ideas into investable, futures orientated infrastructure that advances the Western Cape's developmental agenda.
                </p>
            </div>
        </div>
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-10 px-xl-20  ps-lg-5 ps-xl-5">
            <img
                src={toAbsoluteUrl("media/images/the-outcome.png")}
                alt="Investment-Ready Portfolio Outcome"
                className="w-100 h-100"
                loading="lazy"
            />
        </div>
    </div>
));

// Reusable list renderer
const FeatureList: React.FC<{ items: Feature[] }> = memo(({ items }) => (
    <ul className="fs-5 fw-light">
        {items.map((item) => (
            <li key={item.id} className="section-content mb-3">
                {item.text}
            </li>
        ))}
    </ul>
));

const VisionSection: React.FC = memo(() => (
    <div className="row section-padding bg-white align-items-center">
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 pe-lg-5 pe-xl-5">
            <img
                src={toAbsoluteUrl("media/images/western-cap-infrastructure.png")}
                alt="Western Cape Infrastructure Vision"
                className="w-100 h-100"
                loading="lazy"
            />
        </div>
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 ps-lg-5 ps-xl-5" >
            <div>
                <p className="penoptic-evaluation-section-title mb-2">Critical Nature of WCIF 2050 Vision</p>
                <p className="section-content">
                    Curious about how tomorrow's Western Cape comes to life? This platform offers a gateway enabling strategic, innovative, and meaningful projects that shape the region's future. Here, evaluation isn't a hurdle; it's a launchpad for ideas with real impact.
                </p>
                <p className="fs-3">What Awaits You:</p>
                <FeatureList items={FEATURES_DATA} />
            </div>
        </div>
    </div>
));

const EngagementSection: React.FC = memo(() => (
    <div className="row flex-column-reverse flex-lg-row g-10 section-padding bg-white align-items-center">
        <div className="col-md-8 left-content px-10 px-lg-20 px-md-10 px-sm-15">
            <div>
                <p className="penoptic-evaluation-section-title mb-2">Why Engage?</p>
                <p className="section-content">
                    Because here, strategy meets action. We are tying the scientific basis of infrastructure to strategic infrastructure design and implementation. Only the most promising, policy-aligned, and thoroughly prepared projects reach the portfolio - saving you time, building trust, and ensuring every opportunity is worth your attention.
                </p>
                <p className="fs-3">Innovation at Your Fingertips:</p>
                <FeatureList items={ENGAGEMENT_DATA} />
                <p className="penoptic-evaluation-section-title pt-10 mb-3">From Visionary concepts to Investor-Readiness</p>
                <p className="section-content">
                    When a project or concept meets the compliance and regularity benchmarks, it moves into our formal Investment-Ready Portfolio – This ensures the Western Cape pursues economic growth and social impact and ecological conscious designs.
                </p>
            </div>
        </div>
        <div className="col-md-4 px-10 px-sm-15 px-md-10 px-lg-0">
            <img
                src={toAbsoluteUrl("media/images/WhyEngage.jpg")}
                alt="Why Engage with WC Infrastructure"
                className="w-100 "
                loading="lazy"
            />
        </div>
    </div>
));

// Main Component
const ProjectEvaluation: React.FC = () => {
    const dashboardEntry = useDashboardTabEntry('evaluation', 'Project Evaluation');
    return (
        <div className="overflow-y-auto overflow-x-hidden about-us bg-white">
            <HeroSection videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/project-evaluation-new.mp4`}
                fallbackImage={`${envConfig.APP_MEDIA_URL}images/public-pages/project-evaluation-new.webp`}
                title="WC Infrastructure Project Pipeline"
                subtitle="Pioneering the Future: Project Evaluation for Tomorrow's Western Cape"
                imageSrc={toAbsoluteUrl('media/images/project-evoluation.png')}
                imageAlt="Project Evaluation"
                imageClassName="w-150px"
                {...dashboardEntry}
                scrollParentId="introduction-section" />
            <IntroductionSection />
            <CardsSection />
            <OutcomeSection />
            <VisionSection />
            <EngagementSection />
        </div>
    );
}

export default ProjectEvaluation;